'use client'

import { OverDialogLayer } from '@fukku/dialog/OverDialogLayer/OverDialogLayer'
import { useOutsideClick } from 'hooks/useOutsideClick/useOutsideClick'
import {
	cloneElement,
	type ForwardedRef,
	forwardRef,
	type JSX,
	type ReactNode,
	type RefObject,
	useId,
	useImperativeHandle,
	useRef,
} from 'react'
import { createPortal } from 'react-dom'
import { TooltipContent } from './components/TooltipContent'
import { useTooltipPosition } from './hooks/useTooltipPosition'
import { useTooltipState } from './hooks/useTooltipState'
import { handleKeyDown } from './utils/utils'

/**
 * Minimum distance to the edge of the window
 */
const MIN_DISTANCE_BOUNDARY = 10

export type Position = 'bottom' | 'top' | 'left' | 'right'

export interface TooltipProps {
	readonly content: string | ReactNode
	readonly position?: Position
	readonly offset?: number
	readonly boundary?: {
		current?: HTMLElement | null
	}
	readonly asChildOf?: HTMLElement
	readonly key?: number | string
	readonly children?: JSX.Element
	readonly className?: string
	readonly onOpen?: () => void
	readonly onClose?: () => void
	readonly closeDelay?: number
	readonly preventHover?: boolean
	readonly reverse?: boolean
	readonly 'data-testid'?: string
	/**
	 * hideToScreenReader - Flag to indicate if the tooltip should be hidden from screen readers
	 * because the trigger element has the same text as the tooltip
	 */
	readonly hideToScreenReader?: boolean
	readonly hideOnKeyDown?: boolean
}

export interface TooltipHandle {
	open: () => void
	close: () => void
	toggle: () => void
}

export interface TooltipChildrenProps {
	readonly 'aria-describedby'?: string
	readonly 'aria-expanded'?: boolean
	readonly 'aria-haspopup'?: boolean
	readonly onMouseEnter?: () => void
	readonly onMouseLeave?: () => void
	readonly onFocus?: () => void
	readonly onBlur?: () => void
	readonly onKeyDown?: (e: KeyboardEvent) => void
	readonly ref?: RefObject<HTMLDivElement | null>
	readonly key?: number | string
	readonly 'data-testid'?: string
	readonly className?: string
	readonly style?: React.CSSProperties
}

// Given useOutsideClick hook is conditionally needed it has been moved to this component to use
// it only if the tooltip has `preventHover`
const OnClickOutsideListener = ({
	tooltipRef,
	handleClose,
}: {
	readonly tooltipRef: RefObject<HTMLDivElement | null>
	readonly handleClose: () => void
}) => {
	useOutsideClick(tooltipRef, () => handleClose())
	return null
}

export const Tooltip = forwardRef<TooltipHandle, Readonly<TooltipProps>>(
	(
		tooltipProps: Readonly<TooltipProps>,
		ref: ForwardedRef<TooltipHandle | null>
	) => {
		const {
			asChildOf = null,
			boundary,
			children,
			className,
			content,
			key,
			offset = 0,
			position = 'bottom',
			preventHover = false,
			closeDelay = 800,
			onClose = () => {
				/* do nothing */
			},
			onOpen = () => {
				/* do nothing */
			},
			reverse = false,
			'data-testid': dataTestId,
			hideToScreenReader = false,
			hideOnKeyDown = true,
		} = tooltipProps

		const uniqueId = useId()

		/**
		 * @constant @type {React.RefObject}
		 */
		const triggerRef = useRef<HTMLDivElement | null>(null)

		/**
		 * @constant @type {React.RefObject}
		 */
		const tooltipRef = useRef<HTMLDivElement | null>(null)

		const {
			show,
			shouldDisplay,
			handleOpen,
			handleClose,
			handleCloseImmediate,
			handleCloseAnimation,
			cancelCloseTimeout,
		} = useTooltipState({
			closeDelay,
			onOpen,
			onClose,
		})

		const { tooltipStyles, arrowStyles } = useTooltipPosition({
			show,
			position,
			offset,
			reverse,
			content,
			tooltipRef,
			triggerRef,
			boundary,
			minDistance: MIN_DISTANCE_BOUNDARY,
		})

		/**
		 * Exposes the API in component reference
		 */
		useImperativeHandle(ref, () => ({
			/**
			 * Open the tooltip
			 */
			open: handleOpen,
			/**
			 * Close the tooltip
			 */
			close: handleCloseImmediate,
			/**
			 * Toggle the tooltip
			 */
			toggle: show ? handleCloseImmediate : handleOpen,
		}))

		const tooltipBody = (
			<TooltipContent
				uniqueId={uniqueId}
				className={className}
				tooltipStyles={tooltipStyles}
				dataTestId={dataTestId}
				preventHover={preventHover}
				cancelCloseTimeout={cancelCloseTimeout}
				handleClose={handleClose}
				shouldDisplay={shouldDisplay}
				handleCloseAnimation={handleCloseAnimation}
				content={content}
				tooltipRef={tooltipRef}
				arrowStyles={arrowStyles}
			/>
		)

		const tooltipContainer = asChildOf ? (
			createPortal(tooltipBody, asChildOf)
		) : (
			<OverDialogLayer>{tooltipBody}</OverDialogLayer>
		)

		return (
			<>
				{children &&
					cloneElement(children, {
						onMouseEnter: preventHover ? undefined : handleOpen,
						onMouseLeave: preventHover ? undefined : handleClose,
						onFocus: preventHover ? undefined : handleOpen,
						onBlur: handleClose,
						onKeyDown: (e: KeyboardEvent) => {
							handleKeyDown({
								handleClose: handleCloseImmediate,
								handleOpen,
								e,
								show,
								hideOnKeyDown,
							})
						},
						ref: triggerRef,
						'aria-describedby':
							show && !hideToScreenReader ? uniqueId : undefined,
						'aria-expanded': show,
						'aria-haspopup': 'true',
						key,
					})}

				{preventHover && show && (
					<OnClickOutsideListener
						tooltipRef={tooltipRef}
						handleClose={handleClose}
					/>
				)}
				{show ? tooltipContainer : null}
			</>
		)
	}
)

Tooltip.displayName = 'Tooltip'
